// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useRef, useState } from 'react'
import {
  Microphone,
  MicrophoneSlash,
  VideoCamera,
  VideoCameraSlash,
} from '@phosphor-icons/react'
import { useI18n } from '../i18n'

/** Devices là lựa chọn thiết bị mà người dùng mang theo vào phòng. */
export type Devices = { mic: boolean; cam: boolean }

const STORE_KEY = 'zmeet_devices'

/**
 * loadDevices đọc lựa chọn của lần trước.
 *
 * Nhớ lại chứ không hỏi lại từ đầu mỗi lần: người quen vào họp với micro tắt
 * làm việc đó mọi lần, và bắt họ bấm lại cùng một nút mỗi ngày là biến một tiện
 * ích thành một thủ tục. Mặc định khi chưa có gì là BẬT cả hai — đó là điều
 * người vào một cuộc họp lần đầu mong đợi.
 */
export function loadDevices(): Devices {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (!raw) return { mic: true, cam: true }
    const d = JSON.parse(raw) as Partial<Devices>
    return { mic: d.mic !== false, cam: d.cam !== false }
  } catch {
    // localStorage bị chặn (chế độ riêng tư, chính sách của tổ chức) không phải
    // lý do để không vào họp được.
    return { mic: true, cam: true }
  }
}

function saveDevices(d: Devices) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(d))
  } catch {
    // Không nhớ được thì thôi; lựa chọn của lần này vẫn có hiệu lực.
  }
}

/**
 * PreJoin — chọn micro và camera TRƯỚC khi cả phòng nhìn thấy mình.
 *
 * # Vì sao có màn hình này
 *
 * Trước đây client nối vào phòng rồi bật thẳng cả micro lẫn camera. Hệ quả là
 * mọi thứ chưa sẵn sàng đều được phát trực tiếp: căn phòng chưa dọn, người thứ
 * hai trong khung hình, tiếng ồn của quán cà phê. Sửa được sau ba giây, nhưng
 * ba giây ấy đã có mười người nhìn thấy.
 *
 * # Xem trước là một nửa của tính năng, không phải trang trí
 *
 * Nút bật/tắt một mình chỉ trả lời "tôi có muốn phát không". Cái người ta thật
 * sự cần biết là "phát ra thì trông thế nào" — đúng camera chưa, ngược sáng
 * không, tóc tai ra sao. Không có khung xem trước thì câu hỏi đó chỉ được trả
 * lời sau khi cả phòng đã có câu trả lời.
 *
 * # Track xem trước bị DỪNG trước khi vào phòng
 *
 * LiveKit tự mở track của nó khi nối. Giữ luôn track xem trước nghĩa là hai
 * đường cùng giữ một camera — trên Windows và một số webcam ngoài, đường thứ
 * hai chỉ nhận được khung hình đen.
 */
export function PreJoin({
  title,
  name,
  devices,
  onChange,
  onJoin,
  busy,
  children,
}: {
  title: string
  name: string
  devices: Devices
  onChange: (d: Devices) => void
  onJoin: () => void
  busy: boolean
  /** Ô nhập mật mã và tên, do màn hình cha quyết định có hiện hay không. */
  children?: React.ReactNode
}) {
  const { t } = useI18n()
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [camError, setCamError] = useState('')

  // Mở và đóng camera xem trước theo đúng cờ `cam`.
  //
  // Hiệu ứng này là chỗ duy nhất chạm vào thiết bị ở màn hình này, và nó dọn
  // sạch ở mọi đường ra: đổi lựa chọn, rời màn hình, hoặc vào phòng. Thiếu một
  // đường là để lại một đèn camera sáng sau khi người dùng đã tắt nó — thứ phá
  // huỷ lòng tin nhanh hơn bất kỳ lỗi nào khác trong một ứng dụng họp.
  useEffect(() => {
    let alive = true

    const stop = () => {
      streamRef.current?.getTracks().forEach((tr) => tr.stop())
      streamRef.current = null
      if (videoRef.current) videoRef.current.srcObject = null
    }

    if (!devices.cam) {
      stop()
      setCamError('')
      return
    }

    // CHỈ xin video. Micro không cần xem trước, và xin quyền cho một thiết bị
    // không dùng tới là cách người dùng học được thói quen bấm "Chặn".
    navigator.mediaDevices
      ?.getUserMedia({ video: true })
      .then((s) => {
        if (!alive) {
          s.getTracks().forEach((tr) => tr.stop())
          return
        }
        streamRef.current = s
        if (videoRef.current) videoRef.current.srcObject = s
        setCamError('')
      })
      .catch(() => {
        if (!alive) return
        // Nói thẳng và TẮT cờ camera. Im lặng ở đây nghĩa là người dùng bấm
        // "Vào phòng" với camera họ tưởng đang bật, rồi mới phát hiện ra.
        setCamError(t('ui.prejoin.camera_failed'))
        onChange({ ...devices, cam: false })
      })

    return () => {
      alive = false
      stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices.cam])

  const toggle = (patch: Partial<Devices>) => {
    const next = { ...devices, ...patch }
    onChange(next)
    saveDevices(next)
  }

  return (
    <main className="center">
      <div className="card prejoin">
        <h1>{title}</h1>

        <div className="preview">
          {devices.cam ? (
            // muted là BẮT BUỘC: không có nó thì micro của mình phát ra loa của
            // mình và tạo hú ngay ở màn hình chuẩn bị.
            <video ref={videoRef} autoPlay playsInline muted />
          ) : (
            <div className="placeholder">
              {(name.trim() || t('ui.join.guest_name')).slice(0, 1).toUpperCase()}
            </div>
          )}

          <div className="devices">
            <button
              className={'icon sec' + (devices.mic ? '' : ' off')}
              aria-pressed={devices.mic}
              aria-label={t(devices.mic ? 'ui.prejoin.mic_off' : 'ui.prejoin.mic_on')}
              onClick={() => toggle({ mic: !devices.mic })}
            >
              {devices.mic ? (
                <Microphone size={20} weight="light" />
              ) : (
                <MicrophoneSlash size={20} weight="light" />
              )}
            </button>
            <button
              className={'icon sec' + (devices.cam ? '' : ' off')}
              aria-pressed={devices.cam}
              aria-label={t(devices.cam ? 'ui.prejoin.camera_off' : 'ui.prejoin.camera_on')}
              onClick={() => toggle({ cam: !devices.cam })}
            >
              {devices.cam ? (
                <VideoCamera size={20} weight="light" />
              ) : (
                <VideoCameraSlash size={20} weight="light" />
              )}
            </button>
          </div>
        </div>

        {/* Nói ra trạng thái bằng CHỮ, không chỉ bằng màu của cái nút. Một
            người mù màu, hoặc một người đang vội, đọc dòng này nhanh hơn giải
            mã hai biểu tượng. */}
        <p className="sub">
          {t(
            devices.mic
              ? devices.cam
                ? 'ui.prejoin.state_both'
                : 'ui.prejoin.state_mic'
              : devices.cam
                ? 'ui.prejoin.state_cam'
                : 'ui.prejoin.state_none',
          )}
        </p>

        {children}

        <button className="wide" disabled={busy} onClick={onJoin}>
          {t(busy ? 'ui.join.calling' : 'ui.join.join')}
        </button>

        {camError && <div className="msg err">{camError}</div>}
      </div>
    </main>
  )
}
